import AllForm from '../../Stateless/Form';
import { Box, Button, CircularProgress } from '@mui/material';

import { useThemeColors } from '../../../hooks/useThemeColors';
import { useAtom } from 'jotai';
import { UseCreateCMS, useEditCMS } from '../../Query/CMSQuery';
import { CMSAtom } from './ManageCMSTable';
import { createCMSValidationSchema } from '../../HelperFunctions/validationUtils';
import { COLORS } from '../../../theme/themeConstants';

interface CMSFormValues {
  id?: string;
  pageTitle: string;
  pageTitleInArabic: string;
  pageDescription: string;
  pageDescriptionInArabic: string;
}

const validationSchema = createCMSValidationSchema();

const AddCMS = ({ onCancel }: { onCancel: () => void }) => {
  const [CMSData] = useAtom(CMSAtom);
  const { warning } = useThemeColors();
  const { mutate: CreateCMS, isPending: CreateCMSLoading } = UseCreateCMS();
  const { mutate: EditCMS, isPending: EditCMSLoading } = useEditCMS();

  return (
    <>
      {AllForm({
        initialValues: {
          pageTitle: CMSData?.pageTitle || '',
          pageTitleInArabic: CMSData?.pageTitleInArabic || '',
          pageDescription: CMSData?.pageDescription || '',
          pageDescriptionInArabic: CMSData?.pageDescriptionInArabic || '',
        },
        validationSchema,
        validateOnChange: true,
        enableReinitialize: true,
        onSubmit: async (values: Record<string, any>) => {
          const modifiedValues: CMSFormValues = {
            pageTitle: values.pageTitle,
            pageTitleInArabic: values.pageTitleInArabic,
            pageDescription: values.pageDescription,
            pageDescriptionInArabic: values.pageDescriptionInArabic,
          };
          try {
            if (CMSData?.id) {
              EditCMS(
                {
                  id: CMSData.id,
                  data: modifiedValues,
                },
                {
                  onSuccess: () => {
                    onCancel();
                  },
                  onError: (error: any) => {
                    console.log(error);
                  },
                }
              );
            } else {
              CreateCMS(modifiedValues, {
                onSuccess: () => {
                  onCancel();
                },
                onError: (error: any) => {
                  console.error(error);
                },
              });
            }
          } catch (error) {
            console.error('Error submitting form:', error);
            // Optionally, you can show an error message to the user
          }
        },
        fields: [
          {
            name: 'pageTitle',
            label: 'Page Title:',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'pageTitleInArabic',
            label: 'Page Title (In Arabic):',
            type: 'text',
            isRequired: true,
          },
          {
            name: 'pageDescription',
            label: 'Page Description (In English):',
            type: 'markdown',
            isRequired: true,
          },
          {
            name: 'pageDescriptionInArabic',
            label: 'Page Description (In Arabic):',
            type: 'markdown',
            isRequired: true,
          },
        ],
        customButton: (formikProps: any) => (
          <Box sx={{ display: 'flex', gap: 2, px: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: 14,
                px: 3,
                py: 1,
                backgroundColor: COLORS.primary.main,
                '&:hover': {
                  backgroundColor: COLORS.primary.dark,
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
              onClick={(e) => {
                e.preventDefault();
                // Mark all fields as touched to show validation errors
                Object.keys(formikProps.values).forEach((fieldName) => {
                  formikProps.setFieldTouched(fieldName, true, false);
                });
                // Check if there are validation errors
                if (Object.keys(formikProps.errors).length === 0) {
                  formikProps.handleSubmit();
                } else {
                  console.log('Validation failed:', formikProps.errors);
                }
              }}
              disabled={CreateCMSLoading || EditCMSLoading}
            >
              {CreateCMSLoading || EditCMSLoading ? (
                <>
                  <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                  {CMSData ? 'Updating...' : 'Creating...'}
                </>
              ) : CMSData ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>

            {!CMSData && (
              <Button
                type="button"
                variant="outlined"
                onClick={formikProps.resetForm}
                sx={{
                  textTransform: 'none',
                  color: COLORS.secondary.main,
                  borderColor: COLORS.secondary.main,
                  '&:hover': {
                    color: '#333',
                    borderColor: '#333',
                  },
                }}
              >
                Reset
              </Button>
            )}

            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={onCancel}
              sx={{
                bgcolor: warning.main,
                color: '#fff',
                '&:hover': {
                  bgcolor: warning.dark,
                  color: '#fff',
                },
              }}
            >
              Cancel
            </Button>
          </Box>
        ),
      })()}
    </>
  );
};

export default AddCMS;
